import * as React from 'react';
import { Chip, IconButton, Theme, Tooltip, useTheme } from '@mui/material';
import { Company, Country, Group, ReadinessGaugeInstance } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { ReactComponent as EditIcon } from '../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/Delete.svg';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../context/GroupContext';
import { CompanyContext } from '../../context/CompanyContext';
import { log } from '../../util/log';
import DeleteConfirmModal from '../shared/modal/DeleteConfirmModal';
import {
  deleteReadinessGaugeInstance,
  readiness_gauge_instance_label
} from '../../lib/readiness_gauge/readiness_gauge_instance';
import { ReactComponent as GaugeIcon } from '../../assets/icons/Gauge.svg';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import DataGridLoading from '../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../shared/datagrid/DataGridToolbar';
import { ViewAccess, getViewAccess } from '../../util/user_access';
import { UserContext } from '../../context/UserContext';

interface ReadinessGaugeGridProps {
  readiness_gauge_rows: Array<ReadinessGaugeInstance>;
  handleUpdateReadinessGaugeRows: (rows_data: Array<ReadinessGaugeInstance>) => void;
}

interface ReadinessGaugeEntity {
  name: string;
  level: string;
}

/**
 * Datagrid handling the display of Readiness Gauge Instances on Group, Company and Country level.
 * @param {Array<ReadinessGaugeInstance>} readiness_gauge_rows Readiness Gauge Instance objects from database from parent.
 * @param {void} handleUpdateReadinessGaugeRows Update Readiness Gauge Instance objects in parent.
 * @returns {JSX.Element}
 */
const ReadinessGaugeGrid = ({
  readiness_gauge_rows,
  handleUpdateReadinessGaugeRows
}: ReadinessGaugeGridProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { user } = React.useContext(UserContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const [actionRow, setActionRow] = React.useState<ReadinessGaugeInstance | null>(null);
  const [openDialogDelete, setOpenDialogDelete] = React.useState<boolean>(false);
  const theme: Theme = useTheme();
  const navigate: NavigateFunction = useNavigate();

  const view_access: ViewAccess =
    user && group && company
      ? getViewAccess(user, group.id, company.id, 'sustainability.readiness_gauge')
      : null;
  const view_only: boolean = view_access === 'read';

  const getReadinessGaugeEntity = (
    group: Group,
    readiness_gauge_instance: ReadinessGaugeInstance
  ): ReadinessGaugeEntity => {
    if (readiness_gauge_instance.country) {
      return {
        name: (readiness_gauge_instance.country as Country).name,
        level: 'Country'
      };
    } else if (readiness_gauge_instance.company) {
      return {
        name: (readiness_gauge_instance.company as Company).name,
        level: 'Company'
      };
    }
    return {
      name: group.name,
      level: 'Group'
    };
  };

  // Handler functions
  const handleCreateClick = (): void => {
    navigate('/sustainability/readiness/questionnaire');
  };

  const handleResultsClick = (readiness_gauge_instance: ReadinessGaugeInstance): void => {
    navigate('/sustainability/readiness/results', {
      state: {
        group: group,
        readinessGauge: readiness_gauge_instance
      }
    });
  };

  const handleEditClick = (readiness_gauge_instance: ReadinessGaugeInstance): void => {
    navigate('/sustainability/readiness/questionnaire', {
      state: {
        readinessGauge: readiness_gauge_instance,
        readinessGaugeParameters: {
          company: readiness_gauge_instance.company
            ? {
                ...readiness_gauge_instance.company,
                group: group
              }
            : null,
          country: readiness_gauge_instance.country
        },
        questionnaireName: readiness_gauge_instance.name
      }
    });
  };

  const handleDeleteClick = async (row: ReadinessGaugeInstance): Promise<void> => {
    setActionRow(row);
    setOpenDialogDelete(true);
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const columns: Array<GridColDef> = [
    {
      field: 'entity',
      headerName: 'Entity',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return group ? getReadinessGaugeEntity(group, params.row).name : '';
      },
      flex: 1
    },
    {
      field: 'level',
      headerName: 'Level',
      headerAlign: 'left',
      align: 'left',
      valueGetter: (params: GridValueGetterParams) => {
        return group ? getReadinessGaugeEntity(group, params.row).level : '';
      },
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'completed',
      headerName: 'Status',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            label={params.row.completed ? 'Completed' : 'Draft'}
            color={params.row.completed ? 'success' : 'info'}
            variant="outlined"
          />
        );
      },
      flex: 1
    },
    {
      field: 'results',
      headerName: 'View Results',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <IconButton
            disabled={!params.row.completed}
            sx={{
              color: theme.palette.primary.main
            }}
            onClick={() => handleResultsClick(params.row)}
          >
            <GaugeIcon height={35} fill="currentColor" stroke="currentColor" strokeWidth={2} />
          </IconButton>
        );
      },
      flex: 1
    }
  ];
  if (!view_only) {
    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            {!row.completed && (
              <GridActionsCellItem
                key={1}
                onClick={() => handleEditClick(row)}
                icon={
                  <Tooltip title="Edit Gauge">
                    <EditIcon width={25} fill={theme.palette.primary.main} />
                  </Tooltip>
                }
                size="large"
                label="Edit"
                sx={{
                  color: 'primary.main'
                }}
              />
            )}
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Gauge">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    });
  }

  const deleteRow = async (readiness_gauge: ReadinessGaugeInstance): Promise<void> => {
    try {
      if (group) {
        await deleteReadinessGaugeInstance(readiness_gauge.id, readiness_gauge.name, group.id).then(
          () => {
            handleUpdateReadinessGaugeRows(
              readiness_gauge_rows.filter((row: ReadinessGaugeInstance) => {
                return row.id !== readiness_gauge.id;
              })
            );
          }
        );
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ReadinessGaugeGrid failed on an unknown error while calling deleteRow.',
          {
            group: group,
            company: company,
            readiness_gauge: readiness_gauge
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove Readiness Gauge. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setOpenDialogDelete(false);
    }
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={true}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && actionRow.name}
        handleDelete={actionRow && (() => deleteRow(actionRow))}
        allow_archive={false}
      />

      {/* Interactive Data Table */}
      <DataGrid
        autoHeight
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10, page: 0 }
          },
          sorting: {
            sortModel: [{ field: 'modified', sort: 'desc' }]
          }
        }}
        hideFooter={readiness_gauge_rows.length > 1 ? false : true}
        columnHeaderHeight={readiness_gauge_rows.length < 1 ? 0 : 56}
        pageSizeOptions={[10, 25, 50, 100]}
        rows={readiness_gauge_rows}
        columns={columns}
        disableRowSelectionOnClick
        slots={{
          toolbar: () => (
            <DataGridToolbar
              entity_label={readiness_gauge_instance_label}
              handleCreate={view_only ? undefined : handleCreateClick}
            />
          ),
          noRowsOverlay: () => <DataGridNoRows missing_label={readiness_gauge_instance_label} />,
          loadingOverlay: () => <DataGridLoading columns={columns.length} />
        }}
        sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
      />
    </>
  );
};

export default ReadinessGaugeGrid;
